import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { ArrowLeftRight, Moon, Sun, User, LogOut, RotateCcw, Zap, X, Repeat2, Share2 } from 'lucide-react';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import SaveMatch from './SaveMatch';
import MatchDisplay from './MatchDisplay';
import Scorecard from './Scorecard';
import Timer from './Timer';
import TeamSearch from './TeamSearch';
import MatchGrouping from './MatchGrouping';
import MatchGroupDetails from './MatchGroupDetails';
import Statistics from './Statistics';
import OfflineStatus from './OfflineStatus';
import StateRestorationDialog from './StateRestorationDialog';
import { useOffline } from '../hooks/useOffline';
import { useScoreboardPersistence } from '../hooks/useScoreboardPersistence';
import { aceEntryFor, isAce, isTeamAPoint, isTeamBPoint, getServeRotationStats } from '../utils/scoringOrder';
import { elapsedMatchSeconds, timeoutSecondsLeft } from '../utils/matchClock';
import './ScoreBoard.css';

const TIMEOUT_SECONDS = 60;

function formatTimeoutClock(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

function TimeoutClock({ teamName, remaining, colorClass, onDismiss }) {
  const expired = remaining <= 0;
  const progress = Math.max(0, remaining / TIMEOUT_SECONDS);
  return (
    <div
      className={`timeout-clock ${colorClass}${!expired && remaining <= 10 ? ' ending' : ''}${expired ? ' expired' : ''}`}
      role="timer"
      aria-live="polite"
      aria-label={expired ? 'Timeout over' : `Timeout ${formatTimeoutClock(remaining)} remaining for ${teamName}`}
    >
      <div className="timeout-clock-meta">
        <span className="timeout-clock-kicker">Timeout</span>
        <span className="timeout-clock-team">{teamName}</span>
      </div>
      <div className="timeout-clock-time">{expired ? 'TIME' : formatTimeoutClock(remaining)}</div>
      <div className="timeout-clock-track" aria-hidden="true">
        <div className="timeout-clock-fill" style={{ transform: `scaleX(${progress})` }} />
      </div>
      <button type="button" className="timeout-clock-dismiss" onClick={onDismiss}>
        <X size={14} />
        Done
      </button>
    </div>
  );
}

function stopAceEvent(e) {
  e.stopPropagation();
}

function AceButton({ teamName, onAce, side }) {
  return (
    <button
      type="button"
      className={`ace-btn ace-btn-${side}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onAce();
      }}
      onPointerDown={stopAceEvent}
      onMouseDown={stopAceEvent}
      onKeyDown={(e) => e.stopPropagation()}
      aria-label={`Record ace serve for ${teamName}`}
      title="Record ace serve"
    >
      ACE
    </button>
  );
}

function ScoreBoard({ token, user, onLogout, onProfileClick, nightMode, toggleNightMode, isGuestMode = false, onTeamClick, onMatchClick, editMatch, onEditComplete }) {
  // Initialize offline functionality
  const { isOnline, saveMatch: saveMatchOfflineCapable } = useOffline(token);
  
  // Initialize scoreboard persistence
  const {
    saveScoreboardState,
    loadScoreboardState,
    clearScoreboardState,
    hasActiveState,
    createAutoSaver,
    prepareStateForSaving,
    getRestorationInfo
  } = useScoreboardPersistence(user, isGuestMode);
  
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [showMatches, setShowMatches] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [showScorecard, setShowScorecard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [colorsSwapped, setColorsSwapped] = useState(false);
  const [scoringOrder, setScoringOrder] = useState([]);
  const [servingTeam, setServingTeam] = useState(null);
  const [flashTeamA, setFlashTeamA] = useState(false);
  const [flashTeamB, setFlashTeamB] = useState(false);
  const [timeoutsA, setTimeoutsA] = useState([false, false]);
  const [timeoutsB, setTimeoutsB] = useState([false, false]);
  const [timeoutClock, setTimeoutClock] = useState(null);
  const [timeoutNow, setTimeoutNow] = useState(() => Date.now());
  const clockRef = useRef({ baseSeconds: 0, startedAt: null });
  const isRunningRef = useRef(false);
  const timeoutClockRef = useRef(null);
  isRunningRef.current = isRunning;
  timeoutClockRef.current = timeoutClock;

  const setClockRunning = useCallback((running) => {
    const clock = clockRef.current;
    if (running) {
      if (clock.startedAt == null) {
        clock.startedAt = Date.now();
      }
      setTime(elapsedMatchSeconds(clock));
      setIsRunning(true);
      return;
    }
    if (clock.startedAt != null) {
      clock.baseSeconds = elapsedMatchSeconds(clock);
      clock.startedAt = null;
    }
    setTime(clock.baseSeconds);
    setIsRunning(false);
  }, []);

  const setClockSeconds = useCallback((seconds) => {
    const safe = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
    clockRef.current.baseSeconds = safe;
    if (clockRef.current.startedAt != null) {
      clockRef.current.startedAt = Date.now();
    }
    setTime(safe);
  }, []);

  const resetClock = useCallback(() => {
    clockRef.current = { baseSeconds: 0, startedAt: null };
    setTime(0);
    setIsRunning(false);
  }, []);
  const [hasAttemptedZeroSave, setHasAttemptedZeroSave] = useState(false);
  const [currentView, setCurrentView] = useState('scoreboard'); // 'scoreboard', 'matches', 'teams', 'matchGroups', 'statistics'
  const [selectedMatchGroup, setSelectedMatchGroup] = useState(null);
  const [currentMatchGroup, setCurrentMatchGroup] = useState(null);
  const [matchGroups, setMatchGroups] = useState([]);
  const [showMatchGroupForm, setShowMatchGroupForm] = useState(false);
  const [newMatchGroup, setNewMatchGroup] = useState({
    name: '',
    type: 'League',
    teamA: '',
    teamB: '',
    bestOf: 3,
    note: ''
  });
  const [teamASuggestions, setTeamASuggestions] = useState([]);
  const [teamBSuggestions, setTeamBSuggestions] = useState([]);
  const [currentSetNumber, setCurrentSetNumber] = useState(1);
  const [isPublic, setIsPublic] = useState(false);
  const [liveShare, setLiveShare] = useState(null);
  const [liveShareStarting, setLiveShareStarting] = useState(false);
  const liveShareRef = useRef(null);
  const endLiveShareRef = useRef(async () => {});
  const livePushChain = useRef(Promise.resolve());
  liveShareRef.current = liveShare;
  
  // State restoration
  const [showRestorationDialog, setShowRestorationDialog] = useState(false);
  const [restorationInfo, setRestorationInfo] = useState(null);
  const [hasRestoredState, setHasRestoredState] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    if (!isGuestMode) {
      fetchRecentMatches();
      fetchMatchGroups();
    }
  }, [isGuestMode]);

  // Check for state restoration on component mount
  useEffect(() => {
    if (user && !hasRestoredState) {
      console.log('Checking for restoration on mount...');
      const info = getRestorationInfo();
      if (info && info.hasState) {
        console.log('Found saved state, showing restoration dialog');
        setRestorationInfo(info);
        setShowRestorationDialog(true);
      } else {
        console.log('No saved state found');
      }
      // Set this after a small delay to prevent auto-save from running immediately
      setTimeout(() => setHasRestoredState(true), 100);
    }
  }, [user, hasRestoredState, getRestorationInfo]);

  const fetchRecentMatches = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/matches/recent`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      setRecentMatches(response.data);
    } catch (error) {
      console.error('Error fetching recent matches:', error);
    }
  };

  const fetchMatchGroups = async () => {
    try {
      // Only fetch match groups from the last 5 days for suggestions
      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/match-groups`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
        params: {
          dateFrom: fiveDaysAgo.toISOString()
        }
      });
      setMatchGroups(response.data.matchGroups || []);
    } catch (error) {
      console.error('Error fetching match groups:', error);
    }
  };

  const fetchTeamSuggestions = async (query, field) => {
    if (!query || query.length < 2) {
      if (field === 'teamA') {
        setTeamASuggestions([]);
      } else {
        setTeamBSuggestions([]);
      }
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/matches/suggestions/teams?q=${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (field === 'teamA') {
        setTeamASuggestions(response.data);
      } else {
        setTeamBSuggestions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team suggestions:', error);
    }
  };
  
  useEffect(() => {
    setHasAttemptedZeroSave(false);
  }, [scoreA, scoreB]);

  const handleScore = useCallback((team, isAceServe = false) => {
    console.log(`Score button clicked for team ${team}${isAceServe ? ' (ace)' : ''}`);
    console.log('Current scores before update:', { scoreA, scoreB });
    
    setScoringOrder(prevOrder => [...prevOrder, isAceServe ? aceEntryFor(team) : team]);
    if (team === 'A') {
      setScoreA(prevScore => {
        console.log(`Team A score updating from ${prevScore} to ${prevScore + 1}`);
        return prevScore + 1;
      });
      setFlashTeamA(true);
      setTimeout(() => setFlashTeamA(false), 300);
    } else {
      setScoreB(prevScore => {
        console.log(`Team B score updating from ${prevScore} to ${prevScore + 1}`);
        return prevScore + 1;
      });
      setFlashTeamB(true);
      setTimeout(() => setFlashTeamB(false), 300);
    }
    setServingTeam(team);
    if (!isRunningRef.current) {
      setClockRunning(true);
    }
    
    // Mark as user interaction immediately
    setHasUserInteracted(true);
  }, [scoreA, scoreB, setClockRunning]);

  const startTimeoutClock = useCallback((team) => {
    const started = Date.now();
    setTimeoutNow(started);
    setTimeoutClock({ team, endsAt: started + TIMEOUT_SECONDS * 1000 });
  }, []);

  const dismissTimeoutClock = useCallback(() => {
    setTimeoutClock(null);
  }, []);

  useEffect(() => {
    if (!isRunning) return undefined;

    const tick = () => {
      const next = elapsedMatchSeconds(clockRef.current);
      setTime((prev) => (prev === next ? prev : next));
    };

    tick();
    const intervalId = setInterval(tick, 250);
    const onWake = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('pageshow', onWake);
    };
  }, [isRunning]);

  useEffect(() => {
    if (!timeoutClock) return undefined;

    const tick = () => {
      const current = Date.now();
      setTimeoutNow(current);
      if (current >= timeoutClock.endsAt + 2000) {
        dismissTimeoutClock();
      }
    };

    tick();
    const intervalId = setInterval(tick, 200);
    const onWake = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('pageshow', onWake);
    };
  }, [timeoutClock, dismissTimeoutClock]);

  const toggleTimeout = useCallback((team, index) => {
    const isTeamA = team === 'A';
    const current = isTeamA ? timeoutsA : timeoutsB;
    const wasChecked = current[index];
    const next = [...current];
    next[index] = !wasChecked;
    if (isTeamA) {
      setTimeoutsA(next);
    } else {
      setTimeoutsB(next);
    }

    const entry = `${isTeamA ? 'TA' : 'TB'}${index + 1}`;
    if (!wasChecked) {
      setScoringOrder((prevOrder) => [...prevOrder, entry]);
      startTimeoutClock(team);
    } else {
      setScoringOrder((prevOrder) => {
        const lastIndex = prevOrder.lastIndexOf(entry);
        if (lastIndex === -1) return prevOrder;
        const newOrder = [...prevOrder];
        newOrder.splice(lastIndex, 1);
        return newOrder;
      });
    }
  }, [timeoutsA, timeoutsB, startTimeoutClock]);

  const chartData = useMemo(() => {
    let scoreDataA = 0;
    let scoreDataB = 0;
    return scoringOrder.map((team, index) => {
      if (isTeamAPoint(team)) scoreDataA++;
      if (isTeamBPoint(team)) scoreDataB++;
      // Timeouts (TA1, TA2, TB1, TB2) don't increment scores
      const leadingTeam = scoreDataA > scoreDataB ? teamA || 'Team A' : (scoreDataB > scoreDataA ? teamB || 'Team B' : 'Tie');
      return {
        totalPoints: index + 1,
        [teamA || 'Team A']: scoreDataA,
        [teamB || 'Team B']: scoreDataB,
        pointDifferential: Math.abs(scoreDataA - scoreDataB),
        leadingTeam: leadingTeam,
        barColor: leadingTeam === (teamA || 'Team A') ? "#28a745" : (leadingTeam === (teamB || 'Team B') ? "#dc3545" : "#6c757d")
      };
    });
  }, [scoringOrder, teamA, teamB]);

  const rotationStats = useMemo(() => getServeRotationStats(scoringOrder), [scoringOrder]);

  const handleMatchSaved = useCallback((message = 'Match saved successfully!') => {
    setScoreA(0);
    setScoreB(0);
    setEditingMatch(null);
    resetClock();
    setError('');
    setNote('');
    setScoringOrder([]);
    setIsPublic(false);
    setTimeoutsA([false, false]);
    setTimeoutsB([false, false]);
    setTimeoutClock(null);
    setServingTeam(null);
    liveShareRef.current = null;
    setLiveShare(null);
    setSaveMessage(message);
    setHasAttemptedZeroSave(false);
    
    // Clear the saved scoreboard state since match was saved
    clearScoreboardState();
    // Reset interaction flag since we're starting fresh
    setHasUserInteracted(false);
    
    fetchRecentMatches();
    
    // If part of a match group, increment set number and refresh groups
    if (currentMatchGroup) {
      setCurrentSetNumber(prev => prev + 1);
      fetchMatchGroups();
    }
    
    setIsSaving(false);
    setTimeout(() => setSaveMessage(''), 3000);
  }, [currentMatchGroup, clearScoreboardState, resetClock]);

  const handleEdit = useCallback((match) => {
    setEditingMatch(match);
    setTeamA(match.teamA);
    setTeamB(match.teamB);
    setScoreA(match.scoreA);
    setScoreB(match.scoreB);
    setNote(match.note || '');
    setClockRunning(false);
    setClockSeconds(match.time || 0);
    setScoringOrder(match.scoringOrder || []);
    setIsPublic(match.isPublic || false);
    setCurrentView('scoreboard'); // Switch to scoreboard view for editing
    
    // Derive timeout states from scoring order (for new format) or use saved timeouts (for old format)
    const derivedTimeoutsA = [false, false];
    const derivedTimeoutsB = [false, false];
    
    if (match.scoringOrder) {
      match.scoringOrder.forEach(entry => {
        if (entry === 'TA1') derivedTimeoutsA[0] = true;
        if (entry === 'TA2') derivedTimeoutsA[1] = true;
        if (entry === 'TB1') derivedTimeoutsB[0] = true;
        if (entry === 'TB2') derivedTimeoutsB[1] = true;
      });
    }
    
    // Use derived timeouts if any timeout entries exist in scoring order, otherwise use saved timeouts
    const hasTimeoutEntries = match.scoringOrder?.some(entry => entry.startsWith('T'));
    setTimeoutsA(hasTimeoutEntries ? derivedTimeoutsA : (match.timeoutsA || [false, false]));
    setTimeoutsB(hasTimeoutEntries ? derivedTimeoutsB : (match.timeoutsB || [false, false]));
  }, [setClockRunning, setClockSeconds]);

  // Handle editMatch prop from App.js
  useEffect(() => {
    if (editMatch && !editingMatch) {
      handleEdit(editMatch);
      if (onEditComplete) {
        onEditComplete(); // Clear the editMatch in App.js
      }
    }
  }, [editMatch, editingMatch, handleEdit, onEditComplete]);

  const handleCancelEdit = useCallback(() => {
    endLiveShareRef.current();
    setEditingMatch(null);
    setTeamA('');
    setTeamB('');
    setScoreA(0);
    setScoreB(0);
    setNote('');
    resetClock();
    setScoringOrder([]);
    setIsPublic(false);
    setTimeoutsA([false, false]);
    setTimeoutsB([false, false]);
    setTimeoutClock(null);
    setError('');
    setSaveMessage('');
    setCurrentView('matches'); // Return to matches view
  }, [resetClock]);

  const handleSelectMatchGroup = (matchGroup) => {
    setCurrentMatchGroup(matchGroup);
    // Auto-populate team names from match group
    setTeamA(matchGroup.teamA);
    setTeamB(matchGroup.teamB);
    
    // Calculate current set number based on existing matches
    const setNumber = (matchGroup.matches?.length || 0) + 1;
    setCurrentSetNumber(setNumber);
  };

  const handleCreateMatchGroup = async () => {
    if (!newMatchGroup.name.trim() || !newMatchGroup.teamA.trim() || !newMatchGroup.teamB.trim()) {
      setError('Please fill in match name and both team names.');
      return;
    }

    try {
      const matchGroupData = {
        ...newMatchGroup
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/match-groups`,
        matchGroupData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentMatchGroup(response.data);
      setCurrentSetNumber(1);
      setMatchGroups([response.data, ...matchGroups]);
      setShowMatchGroupForm(false);
      setNewMatchGroup({
        name: '',
        type: 'League',
        teamA: '',
        teamB: '',
        bestOf: 3,
        note: ''
      });
      // Auto-populate scoreboard with the teams from the new match group
      setTeamA(response.data.teamA);
      setTeamB(response.data.teamB);
      setError('');
    } catch (error) {
      setError('Failed to create match group');
    }
  };

  const handleClearMatchGroup = () => {
    setCurrentMatchGroup(null);
    setCurrentSetNumber(1);
    setTeamA('');
    setTeamB('');
  };

  const endLiveShare = useCallback(async () => {
    const current = liveShareRef.current;
    if (!current?.matchId) {
      liveShareRef.current = null;
      setLiveShare(null);
      return;
    }
    liveShareRef.current = null;
    setLiveShare(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (current.createdForShare) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/matches/${current.matchId}`,
          { headers }
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/matches/${current.matchId}`,
          {
            isLive: false,
            clockStartedAt: null,
            time: elapsedMatchSeconds(clockRef.current)
          },
          { headers }
        );
      }
    } catch (error) {
      console.error('Failed to end live share:', error);
    }
  }, [token]);

  const copyLiveLink = useCallback(async () => {
    const url = liveShareRef.current?.url;
    if (!url) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSaveMessage('Live link copied.');
        setTimeout(() => setSaveMessage(''), 3000);
        return;
      }
    } catch (error) {
      console.error('Clipboard copy failed:', error);
    }
    window.prompt('Copy this link to share the match live:', url);
  }, []);

  const startLiveShare = useCallback(async () => {
    if (isGuestMode) {
      setError('Sign in to share a live match.');
      return;
    }
    if (!isOnline) {
      setError('Connect to the internet to share a live match.');
      return;
    }
    if (!teamA.trim() || !teamB.trim()) {
      setError('Enter both team names before sharing.');
      return;
    }
    if (liveShareRef.current?.url) {
      copyLiveLink();
      return;
    }

    setLiveShareStarting(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const clock = clockRef.current;
      const payload = {
        teamA: teamA.trim(),
        teamB: teamB.trim(),
        scoreA,
        scoreB,
        note,
        time: clock.baseSeconds,
        scoringOrder,
        timeoutsA,
        timeoutsB,
        isPublic,
        setNumber: currentSetNumber,
        ...(currentMatchGroup?._id ? { matchGroup: currentMatchGroup._id } : {}),
        isLive: true,
        clockBaseSeconds: clock.baseSeconds,
        clockStartedAt: clock.startedAt
      };

      let match;
      if (editingMatch?._id) {
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/matches/${editingMatch._id}`,
          payload,
          { headers }
        );
        match = response.data;
        if (!match.shareId) {
          const shareResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/matches/${editingMatch._id}/generateShareId`,
            {},
            { headers }
          );
          match = { ...match, shareId: shareResponse.data.shareId };
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/matches`,
          {
            ...payload,
            clientId: `live_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
          },
          { headers }
        );
        match = response.data;
      }

      const url = `${window.location.origin}/share/${match.shareId}`;
      const next = {
        matchId: match._id,
        shareId: match.shareId,
        url,
        createdForShare: !editingMatch
      };
      liveShareRef.current = next;
      setLiveShare(next);
      setSaveMessage('Live link copied.');
      setTimeout(() => setSaveMessage(''), 3000);
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          window.prompt('Copy this link to share the match live:', url);
        }
      } catch (copyError) {
        window.prompt('Copy this link to share the match live:', url);
      }
    } catch (error) {
      console.error('Failed to start live share:', error);
      setError(error.response?.data?.message || 'Could not start the live share link.');
    } finally {
      setLiveShareStarting(false);
    }
  }, [
    isGuestMode, isOnline, teamA, teamB, scoreA, scoreB, note, scoringOrder,
    timeoutsA, timeoutsB, isPublic, currentSetNumber, currentMatchGroup, editingMatch,
    token, copyLiveLink
  ]);

  endLiveShareRef.current = endLiveShare;

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the scoreboard? This will clear all scores, time, and notes but keep the team names.')) {
      endLiveShare();
      setScoreA(0);
      setScoreB(0);
      resetClock();
      setNote('');
      setScoringOrder([]);
      setServingTeam(null);
      setTimeoutsA([false, false]);
      setTimeoutsB([false, false]);
      setTimeoutClock(null);
      setColorsSwapped(false);
      setError('');
      setSaveMessage('');
      setEditingMatch(null);
      setHasAttemptedZeroSave(false);
      setIsPublic(false);
      
      // Clear saved scoreboard state on reset
      clearScoreboardState();
      // Reset interaction flag so auto-save doesn't immediately save empty state
      setHasUserInteracted(false);
    }
  };

  const validateAndSave = useCallback(() => {
    if (!teamA || !teamB) {
      setError('Please enter names for both teams before saving.');
      setIsSaving(false);
      return false;
    }
    setError('');
    return true;
  }, [teamA, teamB]);

  const handleSaveMatch = useCallback(async () => {
    // Guests cannot save matches
    if (isGuestMode) {
      setError('Guests cannot save matches. Please sign up or log in to save matches.');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');
    setError('');
  
    if (!validateAndSave()) {
      setIsSaving(false);
      return;
    }
  
    if (scoreA === 0 && scoreB === 0 && !hasAttemptedZeroSave) {
      setError('Error saving match.  Both scores at zero.  Press save again to override.');
      setHasAttemptedZeroSave(true);
      setIsSaving(false);
      return;
    }
  
    const live = liveShareRef.current;
    if (live) {
      liveShareRef.current = null;
      setLiveShare(null);
      await livePushChain.current;
    }

    try {
      const matchTime = elapsedMatchSeconds(clockRef.current);
      const saveId = editingMatch?._id || live?.matchId || null;
      let matchData;
      
      if (editingMatch || live) {
        // When editing, only send the fields that should be updated
        matchData = { 
          teamA, 
          teamB, 
          scoreA, 
          scoreB, 
          note, 
          time: matchTime, 
          scoringOrder,
          timeoutsA,
          timeoutsB,
          isPublic,
          isLive: false,
          clockStartedAt: null,
          clockBaseSeconds: null
        };
        // Only include matchGroup and setNumber if they were originally part of the match
        if (editingMatch?.matchGroup) {
          matchData.matchGroup = editingMatch.matchGroup;
          matchData.setNumber = editingMatch.setNumber || 1;
        } else if (live && currentMatchGroup?._id) {
          matchData.matchGroup = currentMatchGroup._id;
          matchData.setNumber = currentSetNumber;
        }
      } else {
        // When creating new match, include all fields including current match group info
        matchData = { 
          teamA, 
          teamB, 
          scoreA, 
          scoreB, 
          note, 
          time: matchTime, 
          scoringOrder,
          timeoutsA,
          timeoutsB,
          matchGroup: currentMatchGroup?._id,
          setNumber: currentSetNumber,
          isPublic,
          isLive: false,
          clockStartedAt: null,
          clockBaseSeconds: null
        };
      }
      
      console.log('Sending match data:', matchData);
      console.log('Editing match:', editingMatch ? 'Yes' : 'No');
      console.log('Network status:', isOnline ? 'Online' : 'Offline');
      
      // Use the offline-capable save function
      const saveResult = await saveMatchOfflineCapable(
        matchData, 
        !!saveId, 
        saveId
      );
      
      if (saveResult.success) {
        console.log('Save result:', saveResult);
        
        const successMessage = saveResult.source === 'offline'
          ? 'Match saved on this device. It will sync when you have a connection.'
          : 'Match saved successfully!';
        
        handleMatchSaved(successMessage);
        setHasAttemptedZeroSave(false);
      } else {
        throw new Error(saveResult.message || 'Failed to save match');
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Failed to save match';
      setError(errorMessage);
      setIsSaving(false);
      if (live) {
        liveShareRef.current = live;
        setLiveShare(live);
      }
    }
  }, [teamA, teamB, scoreA, scoreB, note, scoringOrder, editingMatch, validateAndSave, handleMatchSaved, timeoutsA, timeoutsB, hasAttemptedZeroSave, isGuestMode, isPublic, currentMatchGroup, currentSetNumber, isOnline, saveMatchOfflineCapable]);

  const handleSelectMatch = useCallback((match) => {
    setSelectedMatches((prevSelected) => {
      const isAlreadySelected = prevSelected.some((selected) => selected._id === match._id);
      if (isAlreadySelected) {
        return prevSelected.filter((selected) => selected._id !== match._id);
      } else {
        return [...prevSelected, match];
      }
    });
  }, []);

  const handleTimeChange = useCallback((newTime) => {
    setClockSeconds(newTime);
  }, [setClockSeconds]);

  const handleSwapColors = useCallback(() => {
    setColorsSwapped(prev => !prev);
  }, []);

  // State persistence handlers
  const handleRestoreState = useCallback(() => {
    console.log('Restore button clicked');
    const savedState = loadScoreboardState();
    console.log('Loaded saved state:', savedState);
    console.log('Saved state details:', {
      teamA: savedState?.teamA,
      teamB: savedState?.teamB, 
      scoreA: savedState?.scoreA,
      scoreB: savedState?.scoreB,
      time: savedState?.time,
      scoringOrder: savedState?.scoringOrder?.length || 0,
      isPublic: savedState?.isPublic
    });
    console.log('Has active state check:', savedState ? hasActiveState(savedState) : 'no saved state');
    
    if (savedState && hasActiveState(savedState)) {
      console.log('Restoring state...');
      console.log('🔥 ABOUT TO RESTORE SCORES:', { scoreA: savedState.scoreA, scoreB: savedState.scoreB });
      console.log('🔥 FULL SAVED STATE:', savedState);
      
      // Log exact values being restored
      console.log('🔥 EXACT VALUES BEING RESTORED:', {
        teamA: savedState.teamA,
        teamB: savedState.teamB,
        scoreA: savedState.scoreA,
        scoreB: savedState.scoreB,
        scoringOrderLength: savedState.scoringOrder?.length || 0
      });
      
      // Restore all state using flushSync to ensure immediate updates
      flushSync(() => {
        console.log('🔥 INSIDE FLUSHSYNC - Setting scores to:', savedState.scoreA, savedState.scoreB);
        setTeamA(savedState.teamA || '');
        setTeamB(savedState.teamB || '');
        setScoreA(savedState.scoreA || 0);
        setScoreB(savedState.scoreB || 0);
        const baseSeconds = Number.isFinite(savedState.clockBaseSeconds)
          ? savedState.clockBaseSeconds
          : (savedState.time || 0);
        const startedAt = savedState.isRunning
          ? (savedState.clockStartedAt || Date.now())
          : null;
        clockRef.current = { baseSeconds, startedAt };
        setTime(elapsedMatchSeconds(clockRef.current));
        setIsRunning(startedAt != null);
        const savedTimeout = savedState.timeoutClock;
        const timeoutStillActive = Number.isFinite(savedTimeout?.endsAt)
          && savedTimeout.endsAt + 2000 > Date.now();
        if (timeoutStillActive) {
          setTimeoutNow(Date.now());
          setTimeoutClock({ team: savedTimeout.team, endsAt: savedTimeout.endsAt });
        } else {
          setTimeoutClock(null);
        }
        setNote(savedState.note || '');
        setScoringOrder(savedState.scoringOrder || []);
        setServingTeam(savedState.servingTeam || null);
        setTimeoutsA(savedState.timeoutsA || [false, false]);
        setTimeoutsB(savedState.timeoutsB || [false, false]);
        setColorsSwapped(savedState.colorsSwapped || false);
        setCurrentMatchGroup(savedState.currentMatchGroup || null);
        setCurrentSetNumber(savedState.currentSetNumber || 1);
        setIsPublic(savedState.isPublic || false);
        setEditingMatch(savedState.editingMatch || null);
        if (savedState.liveShare?.matchId && savedState.liveShare?.shareId) {
          const restoredShare = {
            ...savedState.liveShare,
            url: savedState.liveShare.url || `${window.location.origin}/share/${savedState.liveShare.shareId}`
          };
          liveShareRef.current = restoredShare;
          setLiveShare(restoredShare);
        } else {
          liveShareRef.current = null;
          setLiveShare(null);
        }
      });
      
      console.log('🔥 AFTER FLUSHSYNC - Current component scores:', { scoreA, scoreB });
      console.log('🔥 SCOREBOARD STATE RESTORED SUCCESSFULLY');
      
      // Mark as user interaction since they chose to restore
      setHasUserInteracted(true);
      
      // Force a re-render to ensure state is applied
      setTimeout(() => {
        console.log('Current component state after restoration:', { 
          scoreA: scoreA, scoreB: scoreB, teamA: teamA, teamB: teamB 
        });
      }, 100);
    } else {
      console.log('No valid state to restore');
    }
    setShowRestorationDialog(false);
    // Ensure auto-save is enabled after restoration
    if (!hasRestoredState) {
      setHasRestoredState(true);
    }
  }, [loadScoreboardState, hasActiveState, hasRestoredState, scoreA, scoreB, teamA, teamB]);

  const handleDiscardState = useCallback(() => {
    clearScoreboardState();
    setShowRestorationDialog(false);
    console.log('Saved scoreboard state discarded');
    // Ensure auto-save is enabled after discarding
    if (!hasRestoredState) {
      setHasRestoredState(true);
    }
    // Don't mark as interaction since they're starting fresh
    setHasUserInteracted(false);
  }, [clearScoreboardState, hasRestoredState]);

  // Create a ref to track the latest state values
  const latestStateRef = useRef({});
  
  // Update the ref whenever state changes
  useEffect(() => {
    const clock = clockRef.current;
    latestStateRef.current = {
      teamA,
      teamB,
      scoreA,
      scoreB,
      time: elapsedMatchSeconds(clock),
      isRunning: clock.startedAt != null,
      clockBaseSeconds: clock.baseSeconds,
      clockStartedAt: clock.startedAt,
      timeoutClock,
      note,
      scoringOrder,
      servingTeam,
      timeoutsA,
      timeoutsB,
      colorsSwapped,
      currentMatchGroup,
      currentSetNumber,
      isPublic,
      editingMatch,
      liveShare
    };
  }, [teamA, teamB, scoreA, scoreB, time, isRunning, timeoutClock, note, scoringOrder, servingTeam, timeoutsA, timeoutsB, colorsSwapped, currentMatchGroup, currentSetNumber, isPublic, editingMatch, liveShare]);

  // Track user interaction to prevent auto-save from running on initial mount
  useEffect(() => {
    if (hasRestoredState && (teamA || teamB || scoreA > 0 || scoreB > 0 || time > 0 || note || scoringOrder.length > 0)) {
      setHasUserInteracted(true);
    }
  }, [hasRestoredState, teamA, teamB, scoreA, scoreB, time, note, scoringOrder]);

  // Auto-save state whenever relevant data changes (only after user interaction)
  useEffect(() => {
    if (user && hasRestoredState && hasUserInteracted) {
      const timeoutId = setTimeout(() => {
        // Use the ref to get the absolute latest state values
        const currentState = prepareStateForSaving(latestStateRef.current);
        
        console.log('🔥 AUTO-SAVING STATE:', currentState);
        console.log('🔥 REF STATE WHEN AUTO-SAVING:', {
          teamA: latestStateRef.current.teamA, 
          teamB: latestStateRef.current.teamB, 
          scoreA: latestStateRef.current.scoreA, 
          scoreB: latestStateRef.current.scoreB,
          scoringOrderLength: latestStateRef.current.scoringOrder?.length || 0
        });
        console.log('🔥 PREPARED STATE FOR SAVING:', currentState);
        console.log('Has active state?', hasActiveState(currentState));
        
        if (hasActiveState(currentState)) {
          saveScoreboardState(currentState);
          console.log('State saved successfully');
        } else {
          console.log('No active state, not clearing (user may have reset)');
        }
      }, 1500); // Shorter delay since we're using refs
      
      return () => clearTimeout(timeoutId);
    }
  }, [
    user, hasRestoredState, hasUserInteracted, prepareStateForSaving, hasActiveState, 
    saveScoreboardState,
    teamA, teamB, scoreA, scoreB, isRunning, timeoutClock, note, scoringOrder, servingTeam,
    timeoutsA, timeoutsB, colorsSwapped, currentMatchGroup, currentSetNumber, isPublic, editingMatch, liveShare
  ]);

  // Flush in-progress scores immediately when the app is backgrounded or the
  // tab is killed, instead of waiting for the debounce timer.
  useEffect(() => {
    const flush = () => {
      if (!user) return;
      const clock = clockRef.current;
      const currentState = prepareStateForSaving({
        ...latestStateRef.current,
        time: elapsedMatchSeconds(clock),
        isRunning: clock.startedAt != null,
        clockBaseSeconds: clock.baseSeconds,
        clockStartedAt: clock.startedAt,
        timeoutClock: timeoutClockRef.current,
      });
      if (hasActiveState(currentState)) {
        saveScoreboardState(currentState);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };

    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, prepareStateForSaving, hasActiveState, saveScoreboardState]);

  useEffect(() => {
    if (!liveShare?.matchId || !token) return undefined;

    let cancelled = false;
    const send = () => {
      livePushChain.current = livePushChain.current.then(async () => {
        const current = liveShareRef.current;
        const state = latestStateRef.current;
        if (!current?.matchId || cancelled || !state.teamA || !state.teamB) return;
        const clock = clockRef.current;
        try {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/matches/${current.matchId}`,
            {
              teamA: state.teamA,
              teamB: state.teamB,
              scoreA: state.scoreA,
              scoreB: state.scoreB,
              note: state.note || '',
              time: clock.baseSeconds,
              scoringOrder: state.scoringOrder,
              timeoutsA: state.timeoutsA,
              timeoutsB: state.timeoutsB,
              isPublic: !!state.isPublic,
              setNumber: state.currentSetNumber || 1,
              ...(state.currentMatchGroup?._id ? { matchGroup: state.currentMatchGroup._id } : {}),
              isLive: true,
              clockBaseSeconds: clock.baseSeconds,
              clockStartedAt: clock.startedAt
            },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
          );
        } catch (error) {
          if (!cancelled) console.error('Live score update failed:', error);
        }
      });
    };

    const debounce = setTimeout(send, 400);
    const heartbeat = setInterval(send, 5000);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
      clearInterval(heartbeat);
    };
  }, [liveShare, token, teamA, teamB, scoreA, scoreB, note, scoringOrder, timeoutsA, timeoutsB, isPublic, currentSetNumber, currentMatchGroup, isRunning]);

  const ScoringOrderTable = useMemo(() => {
    return () => {
      let teamATotal = 0;
      let teamBTotal = 0;

      return (
        <div className="scoring-order-table">
          <h3>Scoring Order</h3>
          <table>
            <thead>
              <tr>
                <th>{teamA || 'Team A'}</th>
                <th>{teamB || 'Team B'}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {scoringOrder.map((team, index) => {
                    if (isTeamAPoint(team)) {
                      teamATotal++;
                      return (
                        <span key={index} className={`score-item${isAce(team) ? ' ace-item' : ''}`} title={isAce(team) ? 'Ace serve' : undefined}>
                          {teamATotal}{isAce(team) ? '★' : ''}
                        </span>
                      );
                    } else if (team.startsWith('TA')) {
                      // Team A timeout
                      return (
                        <span key={index} className="score-item timeout-item">
                          T
                        </span>
                      );
                    }
                    return (
                      <span key={index} className="score-item">
                        -
                      </span>
                    );
                  })}
                </td>
                <td>
                  {scoringOrder.map((team, index) => {
                    if (isTeamBPoint(team)) {
                      teamBTotal++;
                      return (
                        <span key={index} className={`score-item${isAce(team) ? ' ace-item' : ''}`} title={isAce(team) ? 'Ace serve' : undefined}>
                          {teamBTotal}{isAce(team) ? '★' : ''}
                        </span>
                      );
                    } else if (team.startsWith('TB')) {
                      // Team B timeout
                      return (
                        <span key={index} className="score-item timeout-item">
                          T
                        </span>
                      );
                    }
                    return (
                      <span key={index} className="score-item">
                        -
                      </span>
                    );
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    };
  }, [scoringOrder, teamA, teamB]);

  const ScoreChart = useMemo(() => {
    return () => (
      <div className="score-chart">
        <h4>Score Progression</h4>
        <ResponsiveContainer width="100%" height={450}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 15, left: 10, bottom: 30 }}>
            <XAxis 
              dataKey="totalPoints" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Points', position: 'insideBottom', offset: -5, style: { fontSize: '10px' } }} 
            />
            <YAxis 
              yAxisId="left" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }} 
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Diff', angle: 90, position: 'insideRight', style: { fontSize: '10px' } }} 
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="custom-tooltip">
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px' }}>{`Point: ${label}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', color: '#28a745' }}>{`${teamA || 'Team A'}: ${data[teamA || 'Team A']}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', color: '#dc3545' }}>{`${teamB || 'Team B'}: ${data[teamB || 'Team B']}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px' }}>{`Diff: ${data.pointDifferential}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', fontWeight: 'bold' }}>{`Leading: ${data.leadingTeam}`}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              iconSize={10}
            />
            <Bar 
              yAxisId="right" 
              dataKey="pointDifferential" 
              opacity={0.8} 
              name="Point Differential"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.barColor} />
              ))}
            </Bar>
            <Line yAxisId="left" type="monotone" dataKey={teamA || 'Team A'} stroke="#28a745" strokeWidth={2} name={teamA || 'Team A'} />
            <Line yAxisId="left" type="monotone" dataKey={teamB || 'Team B'} stroke="#dc3545" strokeWidth={2} name={teamB || 'Team B'} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }, [chartData, teamA, teamB]);

  return (
    <div className={`scoreboard-container ${nightMode ? 'night-mode' : ''}`}>
      {/* State Restoration Dialog */}
      {showRestorationDialog && (
        <StateRestorationDialog
          restorationInfo={restorationInfo}
          onRestore={handleRestoreState}
          onDiscard={handleDiscardState}
          nightMode={nightMode}
        />
      )}
      
      <header className="sb-header">
        <div className="sb-header-inner">
          <div className="sb-brand">
            <div className="vb-logo" aria-hidden="true">🏐</div>
            <h1>
              Score Tracker
              {editingMatch && <span className="editing-badge">Editing</span>}
            </h1>
          </div>
          <div className="sb-actions">
            <button onClick={toggleNightMode} className="control-btn" aria-label={nightMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              {nightMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!isGuestMode && onProfileClick && (
              <button onClick={onProfileClick} className="control-btn" aria-label="Profile">
                <User size={16} />
              </button>
            )}
            <button onClick={onLogout} className="control-btn" aria-label={isGuestMode ? 'Exit' : 'Logout'}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
        {!isGuestMode && (
          <nav className="sb-nav" aria-label="Main">
            <button onClick={() => setCurrentView('scoreboard')} className={`toggle-btn ${currentView === 'scoreboard' ? 'active' : ''}`}>Scoreboard</button>
            <button onClick={() => setCurrentView('matches')} className={`toggle-btn ${currentView === 'matches' ? 'active' : ''}`}>Matches</button>
            <button
              onClick={() => {
                setCurrentView('matchGroups');
                setSelectedMatchGroup(null);
              }}
              className={`toggle-btn ${currentView === 'matchGroups' ? 'active' : ''}`}
            >Series</button>
            <button onClick={() => setCurrentView('teams')} className={`toggle-btn ${currentView === 'teams' ? 'active' : ''}`}>Teams</button>
            <button onClick={() => setCurrentView('statistics')} className={`toggle-btn ${currentView === 'statistics' ? 'active' : ''}`}>Stats</button>
            {selectedMatches.length > 0 && currentView === 'matches' && (
              <button onClick={() => setShowScorecard(!showScorecard)} className="toggle-btn">
                {showScorecard ? 'Hide card' : 'Scorecard'}
              </button>
            )}
          </nav>
        )}
      </header>
      
      {!isGuestMode && (
        <OfflineStatus token={token} showDetails={false} />
      )}
      
      {isGuestMode && !isOnline && (
        <div className={`guest-offline-notice ${nightMode ? 'night-mode' : ''}`}>
          You're offline. Sign in to save matches without a connection.
        </div>
      )}
      <div className="main-content">
        {!isGuestMode && currentView === 'teams' ? (
          <TeamSearch
            token={token}
            nightMode={nightMode}
            onBack={() => setCurrentView('scoreboard')}
            onTeamClick={onTeamClick}
            onMatchClick={onMatchClick}
          />
        ) : !isGuestMode && currentView === 'matchGroups' && !selectedMatchGroup ? (
          <MatchGrouping 
            token={token} 
            nightMode={nightMode} 
            onSelectGroup={setSelectedMatchGroup}
            onBack={() => {
              setCurrentView('scoreboard');
              fetchMatchGroups(); // Refresh match groups when returning to scoreboard
            }}
          />
        ) : !isGuestMode && currentView === 'matchGroups' && selectedMatchGroup ? (
          <MatchGroupDetails
            token={token}
            matchGroup={selectedMatchGroup}
            nightMode={nightMode}
            onBack={() => setSelectedMatchGroup(null)}
          />
        ) : !isGuestMode && currentView === 'statistics' ? (
          <Statistics 
            token={token} 
            nightMode={nightMode} 
            onBack={() => setCurrentView('scoreboard')}
            onTeamClick={onTeamClick}
            onMatchClick={onMatchClick}
          />
        ) : !isGuestMode && showScorecard && currentView === 'matches' ? (
          <Scorecard selectedMatches={selectedMatches} nightMode={nightMode} />
        ) : currentView === 'scoreboard' || isGuestMode ? (
          <div className="game-area">
            <div className="team-inputs">
              <div className="team-input-container">
                <input
                  type="text"
                  value={teamA}
                  onChange={(e) => {
                    setTeamA(e.target.value);
                    fetchTeamSuggestions(e.target.value, 'teamA');
                  }}
                  placeholder="Team A Name"
                  className={`team-input ${colorsSwapped ? 'team-b-input' : 'team-a-input'}`}
                  disabled={!!currentMatchGroup}
                />
                {teamASuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamASuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => {
                          setTeamA(suggestion);
                          setTeamASuggestions([]);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="team-input-container">
                <input
                  type="text"
                  value={teamB}
                  onChange={(e) => {
                    setTeamB(e.target.value);
                    fetchTeamSuggestions(e.target.value, 'teamB');
                  }}
                  placeholder="Team B Name"
                  className={`team-input ${colorsSwapped ? 'team-a-input' : 'team-b-input'}`}
                  disabled={!!currentMatchGroup}
                />
                {teamBSuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamBSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => {
                          setTeamB(suggestion);
                          setTeamBSuggestions([]);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
             </div>
             
             {/* Match Group Selector */}
             {!isGuestMode && (
               <div className="match-group-selector">
                 {currentMatchGroup ? (
                     <div className="current-match-info">
                       <strong>{currentMatchGroup.name}</strong>
                       <span className="set-number">Set {currentSetNumber}</span>
                       <button onClick={handleClearMatchGroup} className="clear-match-btn" aria-label="Clear series">×</button>
                     </div>
                 ) : (
                     <select 
                       onChange={(e) => {
                         if (e.target.value === 'new') {
                           setShowMatchGroupForm(true);
                         } else if (e.target.value) {
                           const group = matchGroups.find(g => g._id === e.target.value);
                           handleSelectMatchGroup(group);
                         }
                       }}
                       value=""
                       className="match-group-select"
                     >
                       <option value="">Add to a series (optional)</option>
                       {matchGroups.map(group => (
                         <option key={group._id} value={group._id}>
                           {group.name}
                         </option>
                       ))}
                       <option value="new">+ New series</option>
                     </select>
                 )}
                 
                 {showMatchGroupForm && (
                   <div className="new-match-form">
                     <div className="form-row">
                       <input
                         type="text"
                         placeholder="Match name (e.g., ELCO vs Cocalico Varsity)"
                         value={newMatchGroup.name}
                         onChange={(e) => setNewMatchGroup({...newMatchGroup, name: e.target.value})}
                       />
                       <select
                         value={newMatchGroup.type}
                         onChange={(e) => setNewMatchGroup({...newMatchGroup, type: e.target.value})}
                       >
                         <option value="League">League</option>
                         <option value="Tournament">Tournament</option>
                         <option value="Scrimmage">Scrimmage</option>
                         <option value="School">School</option>
                       </select>
                       <select
                         value={newMatchGroup.bestOf}
                         onChange={(e) => setNewMatchGroup({...newMatchGroup, bestOf: parseInt(e.target.value)})}
                       >
                         <option value={1}>Best of 1</option>
                         <option value={3}>Best of 3</option>
                         <option value={5}>Best of 5</option>
                         <option value={7}>Best of 7</option>
                       </select>
                     </div>
                     <div className="form-row">
                       <input
                         type="text"
                         placeholder="Team A name"
                         value={newMatchGroup.teamA}
                         onChange={(e) => setNewMatchGroup({...newMatchGroup, teamA: e.target.value})}
                       />
                       <input
                         type="text"
                         placeholder="Team B name"
                         value={newMatchGroup.teamB}
                         onChange={(e) => setNewMatchGroup({...newMatchGroup, teamB: e.target.value})}
                       />
                     </div>
                     <div className="form-actions">
                       <button onClick={handleCreateMatchGroup} className="create-btn">Create Match</button>
                       <button onClick={() => setShowMatchGroupForm(false)} className="cancel-btn">Cancel</button>
                     </div>
                   </div>
                 )}
               </div>
             )}
            <div className="live-scoreboard-wrap">
            <div className="live-scoreboard">
            <div 
              className={`team team-panel ${colorsSwapped ? 'team-b' : 'team-a'} ${flashTeamA ? 'flash' : ''}`} 
              onClick={() => handleScore('A')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleScore('A'); } }}
              aria-label={`Add point for ${teamA || 'Team A'}`}
            >
              {servingTeam === 'A' && (
                <span className="serving-chip"><Zap size={12} /> Serving</span>
              )}
              <div className="team-name balance">{teamA || 'Team A'}</div>
              <div className="score">{scoreA}</div>
              <AceButton side="left" teamName={teamA || 'Team A'} onAce={() => handleScore('A', true)} />
            </div>
            <div 
              className={`team team-panel ${colorsSwapped ? 'team-a' : 'team-b'} ${flashTeamB ? 'flash' : ''}`} 
              onClick={() => handleScore('B')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleScore('B'); } }}
              aria-label={`Add point for ${teamB || 'Team B'}`}
            >
              {servingTeam === 'B' && (
                <span className="serving-chip"><Zap size={12} /> Serving</span>
              )}
              <div className="team-name balance">{teamB || 'Team B'}</div>
              <div className="score">{scoreB}</div>
              <AceButton side="right" teamName={teamB || 'Team B'} onAce={() => handleScore('B', true)} />
            </div>
          </div>
            {timeoutClock && (
              <TimeoutClock
                teamName={timeoutClock.team === 'A' ? (teamA || 'Team A') : (teamB || 'Team B')}
                remaining={timeoutSecondsLeft(timeoutClock.endsAt, timeoutNow)}
                colorClass={timeoutClock.team === 'A' ? (colorsSwapped ? 'team-b' : 'team-a') : (colorsSwapped ? 'team-a' : 'team-b')}
                onDismiss={dismissTimeoutClock}
              />
            )}
            </div>
            <div className="board-toolbar">
            <div className="timeout-container">
              <div className={`${colorsSwapped ? 'timeout-team-b' : 'timeout-team-a'}`}>
                {timeoutsA.map((timeout, index) => (
                  <label key={index} className="timeout-checkbox" title={`${teamA || 'Team A'} timeout ${index + 1}`}>
                    <input
                      type="checkbox"
                      checked={timeout}
                      onChange={() => toggleTimeout('A', index)}
                    />
                    <span className="checkbox-custom"></span>
                  </label>
                ))}
                TO
              </div>
              <div className={`${colorsSwapped ? 'timeout-team-a' : 'timeout-team-b'}`}>
                TO
                {timeoutsB.map((timeout, index) => (
                  <label key={index} className="timeout-checkbox" title={`${teamB || 'Team B'} timeout ${index + 1}`}>
                    <input
                      type="checkbox"
                      checked={timeout}
                      onChange={() => toggleTimeout('B', index)}
                    />
                    <span className="checkbox-custom"></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="score-controls">
              <button className="button-a" onClick={() => {
                setScoreA(Math.max(0, scoreA - 1));
                setScoringOrder(scoringOrder.slice(0, -1));
              }}>−1</button>
              <button onClick={handleSwapColors} className="swap-colors-btn" title="Swap colors">
                <ArrowLeftRight size={16} />
              </button>
              <button className="button-b" onClick={() => {
                setScoreB(Math.max(0, scoreB - 1));
                setScoringOrder(scoringOrder.slice(0, -1));
              }}>−1</button>
              <button onClick={handleReset} className="reset-btn" title="Reset board">
                <RotateCcw size={15} />
              </button>
            </div>
            <Timer 
                compact
                time={time} 
                isRunning={isRunning} 
                setIsRunning={setClockRunning} 
                onTimeChange={handleTimeChange}
                isEditing={!!editingMatch}
                nightMode={nightMode}
              />
            </div>
            <div
              className="live-stats"
              title="Times the serve changed sides (side-outs)"
              aria-label={`${rotationStats.rotations} serve rotations`}
            >
              <Repeat2 size={14} />
              <strong>{rotationStats.rotations}</strong>
              <span>Rotations</span>
              {(rotationStats.sideOutsA > 0 || rotationStats.sideOutsB > 0) && (
                <span className="live-stats-split">
                  {teamA || 'A'} {rotationStats.sideOutsA} · {teamB || 'B'} {rotationStats.sideOutsB}
                </span>
              )}
            </div>
            <div className="live-share">
              {liveShare ? (
                <>
                  <span className="live-share-badge">Live</span>
                  <input
                    className="live-share-url"
                    readOnly
                    value={liveShare.url}
                    aria-label="Live share link"
                    onFocus={(e) => e.target.select()}
                  />
                  <button type="button" className="live-share-copy" onClick={copyLiveLink}>
                    Copy
                  </button>
                  <button type="button" className="live-share-stop" onClick={endLiveShare}>
                    Stop
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="live-share-start"
                  onClick={startLiveShare}
                  disabled={liveShareStarting}
                >
                  <Share2 size={16} />
                  {liveShareStarting ? 'Starting…' : 'Share live'}
                </button>
              )}
            </div>
            {!isGuestMode && (
              <SaveMatch
                token={token}
                teamA={teamA}
                teamB={teamB}
                scoreA={scoreA}
                scoreB={scoreB}
                time={time}
                note={note}
                onMatchSaved={handleSaveMatch}
                editingMatch={editingMatch}
                validateAndSave={validateAndSave}
                nightMode={nightMode}
                isSaving={isSaving}
                onCancelEdit={handleCancelEdit}
                isPublic={isPublic}
                setIsPublic={setIsPublic}
              />
            )}
            <div className="message-container">
              {error && <div className="error-message">{error}</div>}
              {saveMessage && <div className="success-message">{saveMessage}</div>}
            </div>
            {!isGuestMode && (
              <details className="quiet-details">
                <summary>Note</summary>
                <div className="note-field">
                  <textarea
                    id="match-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Key plays, lineup…"
                    rows="2"
                  />
                </div>
              </details>
            )}
            <details className="quiet-details">
              <summary>Rally & chart</summary>
              <ScoringOrderTable />
              <ScoreChart />
            </details>
          </div>
        ) : !isGuestMode && currentView === 'matches' ? (
          <MatchDisplay
            token={token}
            onEdit={handleEdit}
            nightMode={nightMode}
            onSelectMatch={handleSelectMatch}
            selectedMatches={selectedMatches}
            onMatchClick={onMatchClick}
          />
        ) : null}
      </div>
    </div>
  );
}

function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export default ScoreBoard;
